import { ContentLink, useContentSingletons } from "@robe/client/xcore-ui";
import { RelationValue } from "@robe/core";
import { Box, Flex, Icon, Stack } from "@xcorejs/ui";
import ViewIcon from "components/Icons/16/view.svg";
import { FormControl } from "design-system/xcore/Form";
import Select from "design-system/xcore/Select";
import { useLayout } from "xcore";
import { Products } from "xcore/types";
import { useProductFilters } from "./useProductFilters";
import styled from "styled-components";
import { useEffect, useState } from "react";

type FilterKey = "product-type" | "product-size" | "beam-type";

const ProductFilterWrapper = styled(Box)`
  position: relative;
  width: 100%;

  // Fix for eye icon color xcore js ui icons bug
  &:hover .products-filter-group-icon svg path {
    fill: #e00034 !important;
  }
`;

const ProductFilters = () => {
  const { stringify, localize } = useLayout();
  const [products] = useContentSingletons<[Products]>();
  const { values, options, onChange, subCategories } = useProductFilters();
  const [isDocumentReady, setIsDocumentReady] = useState<boolean>(false);

  // Matches the subcategory type with the filter type to find which options to show in each select
  const findFilterType = (id: number | undefined) => {
    if (!id) {
      return null;
    }

    return (subCategories.find((sc) => sc.contentId === id)?.type ?? "") as FilterKey;
  };

  const leftFilterType = findFilterType(localize(products.values.middlePageLeftFilter?.values?.itemsRelation)?.[0].id);
  const middleFilterType = findFilterType(localize(products.values.middlePageMiddleFilter?.values?.itemsRelation)?.[0].id);
  const rightFilterType = findFilterType(localize(products.values.middlePageRightFilter?.values?.itemsRelation)?.[0].id);

  useEffect(() => {
    if (document) {
      setIsDocumentReady(true);
    }
  }, []);

  return (
    <Box mt="3rem">
      <Stack direction={{ _: "column", md: "row" }} gap={{ _: "1.5rem", sm: "3rem" }}>
        <ProductFilterWrapper role="group">
          <Box id="left_filter_box" position="relative">
            <FormControl labelText={stringify(products.values.leftLabelName)}>
              <Select
                isMulti
                placeholder={stringify(products.values.leftPlaceholderText)}
                value={leftFilterType ? values[leftFilterType] : []}
                options={leftFilterType ? options[leftFilterType] : []}
                onChange={onChange(`${leftFilterType}`)}
                menuPortalTarget={isDocumentReady ? document.getElementById("left_filter_box") : null}
                styles={{
                  menuPortal: (styles: any) => ({
                    ...styles,
                    zIndex: 999999,
                    top: "9.3rem",
                    left: 0,
                  }),
                }}
              />
            </FormControl>

            <BrowseAllLink relation={products.values.middlePageLeftFilter} />
          </Box>
        </ProductFilterWrapper>

        <ProductFilterWrapper role="group">
          <Box id="middle_filter_box" position="relative">
            <FormControl labelText={stringify(products.values.midLabelName)}>
              <Select
                isMulti
                placeholder={stringify(products.values.midPlaceholderName)}
                value={middleFilterType ? values[middleFilterType] : []}
                options={middleFilterType ? options[middleFilterType] : []}
                onChange={onChange(`${middleFilterType}`)}
                menuPortalTarget={isDocumentReady ? document.getElementById("middle_filter_box") : null}
                styles={{
                  menuPortal: (styles: any) => ({
                    ...styles,
                    zIndex: 999999,
                    top: "9.3rem",
                    left: 0,
                  }),
                }}
              />
            </FormControl>

            <BrowseAllLink relation={products.values.middlePageMiddleFilter} />
          </Box>
        </ProductFilterWrapper>

        <ProductFilterWrapper role="group">
          <Box id="right_filter_box" position="relative">
            <FormControl labelText={stringify(products.values.rightLabelName)}>
              <Select
                isMulti
                placeholder={stringify(products.values.rightPlaceholderText)}
                value={rightFilterType ? values[rightFilterType] : []}
                options={rightFilterType ? options[rightFilterType] : []}
                onChange={onChange(`${rightFilterType}`)}
                menuPortalTarget={isDocumentReady ? document.getElementById("right_filter_box") : null}
                styles={{
                  menuPortal: (styles: any) => ({
                    ...styles,
                    zIndex: 999999,
                    top: "9.3rem",
                    left: 0,
                  }),
                }}
              />
            </FormControl>

            <BrowseAllLink relation={products.values.middlePageRightFilter} />
          </Box>
        </ProductFilterWrapper>
      </Stack>
    </Box>
  );
};

export default ProductFilters;

const BrowseAllLink = ({ relation }: { relation: RelationValue | undefined }) => {
  const { stringify } = useLayout();
  const [products] = useContentSingletons<[Products]>();

  if (!relation?.slug || !relation?.type) {
    return null;
  }

  return (
    <Box position="absolute" right={0} top="0.2rem" overflow="hidden">
      <Flex
        transform={{ _: "", md: "translateX(calc(100% - 1.6rem))" }}
        transition="transform 300ms"
        _group={{ _hover: { transform: "translateX(0)" } }}
      >
        <Icon svg={<ViewIcon />} fill="gray" mr="0.9rem" className="products-filter-group-icon" display={{ _: "none", md: "block" }} />

        <ContentLink
          variant="underline"
          content={{ id: relation.id, type: relation.type, slug: relation.slug }}
          lineHeight="1.5rem"
          position="relative"
          overflow="hidden"
          opacity={{ _: 1, md: 0 }}
          transition="opacity 300ms"
          _group={{ _hover: { opacity: 1 } }}
        >
          {/* Fix for icon issues related to role="group" */}
          <span style={{ color: "#e00034" }}>{stringify(products.values.browseAllBtn)}</span>
        </ContentLink>
      </Flex>
    </Box>
  );
};
